import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Shield, Anchor, Flame, Award, Users, Globe, CheckCircle, User } from 'lucide-react';
import ScrollToTop from '../components/ScrollToTop';
import WhatsAppButton from '../components/WhatsAppButton';

const About: React.FC = () => {
  const { t } = useLanguage();

  const stats = [
    { number: '25+', labelKey: 'experienceYears', icon: Award },
    { number: '500+', labelKey: 'completedProjects', icon: CheckCircle },
    { number: '500+', labelKey: 'happyCustomers', icon: Users },
    { number: '45+', labelKey: 'countries', icon: Globe }
  ];

  const values = [
    {
      icon: Shield,
      titleKey: 'quality',
      descKey: 'qualityDesc'
    },
    {
      icon: Users,
      titleKey: 'reliability', 
      descKey: 'reliabilityDesc'
    },
    {
      icon: Flame,
      titleKey: 'innovation',
      descKey: 'innovationDesc'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-100">
      
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center min-h-[600px]">
            
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
              <div>
                <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                  {t('aboutTitle').toUpperCase()}
                </h1>
                <div className="w-16 h-1 bg-amber-500 mb-8"></div>
              </div>
              
              <div className="space-y-6 text-slate-700 leading-relaxed">
                <p className="text-lg">
                  {t('aboutContent1')}
                </p>
                
                <p className="text-lg">
                  {t('aboutContent2')}
                </p>
                
                <p className="text-lg">
                  {t('aboutContent3')}
                </p>
                
                <div className="pt-4">
                  <p className="text-xl font-semibold text-slate-900 uppercase tracking-wide">
                    {t('aboutThankYou')}
                  </p>
                </div>
              </div>
            </motion.div>

           
<motion.div
  initial={{ opacity: 0, x: 50 }}
  animate={{ opacity: 1, x: 0 }}
  transition={{ duration: 0.8 }}
  className="relative"
>
  <div className="relative h-[720px] rounded-lg overflow-hidden shadow-2xl">
    <video
      src="/resimler/video.mp4"  
      autoPlay
      muted
      loop
      playsInline
      className="w-full h-full object-cover"
    />
   
    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent"></div>
  </div>
</motion.div>

          </div>
        </div>
      </section>

      
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-slate-800 p-8 rounded-lg border border-slate-700"
            >
              <h3 className="text-2xl font-bold text-amber-400 mb-6">{t('vision')}</h3>
              <p className="text-slate-300 leading-relaxed">
                {t('visionText')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-slate-800 p-8 rounded-lg border border-slate-700"
            >
              <h3 className="text-2xl font-bold text-amber-400 mb-6">{t('mission')}</h3>
              <p className="text-slate-300 leading-relaxed">
                {t('missionText')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      
      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              {t('statsTitle')}
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 * index, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
              >
                <stat.icon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                <div className="text-3xl font-bold text-slate-900 mb-2">{stat.number}</div>
                <div className="text-slate-700 font-medium">{t(stat.labelKey)}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

     
      <section className="py-16 bg-slate-900">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-amber-400 mb-4">
              {t('companyValues')}
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 * index, duration: 0.6 }}
                viewport={{ once: true }}
                className="text-center p-6 bg-slate-800 rounded-lg border border-slate-700 hover:bg-slate-700 transition-colors duration-300"
              >
                <value.icon className="h-12 w-12 text-amber-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-3 text-white">{t(value.titleKey)}</h3>
                <p className="text-slate-300">{t(value.descKey)}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              {t('ourTeam')}
            </h2>
            <div className="w-24 h-1 bg-amber-500 mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900">Ümit ÇİFTCİ</h3>
              <p className="text-amber-600 font-semibold">{t('generalManager')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900">Talha ÇAKMAK</h3>
              <p className="text-amber-600 font-semibold">{t('personnelManager')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900">R.Atakan SUNA </h3>
              <p className="text-amber-600 font-semibold">{t('Teknik Müdür')}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center p-8 bg-white rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <div className="w-20 h-20 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <User className="h-10 w-10 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-slate-900">Esra YALÇIN</h3>
              <p className="text-amber-600 font-semibold">{t('Muhasabe')}</p>
            </motion.div>
          </div>
        </div>
      </section>

       {/* Call to Action */}
      <section className="py-16 bg-slate-100">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-6 text-slate-900">{t('ctaTitle')}</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto text-slate-700">
              {t('ctaDescription')}
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold py-3 px-8 rounded-lg transition-colors duration-200"
              >
                {t('contactUs')}
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
      
      <ScrollToTop />
      <WhatsAppButton />
    </div>
  );
};

export default About;